import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  AseobiPaymentMetadataRequest,
  PackageCheckoutMutation,
} from "@src/@types/api.d";
import {
  CancelPackageCheckout,
  PackageCheckout,
} from "@src/graphql/mutations/packages.mutations";
import { GetPackageCheckoutStatus } from "@src/graphql/queries/packages.queries";
import { isExpired, packageError } from "../utils/asoebi-packages";

type Quote = PackageCheckoutMutation["generateAsoebiPaymentMetadata"];
export type CheckoutStorage = {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<unknown>;
  removeItem: (key: string) => Promise<unknown>;
};
type Attempt = {
  key: string;
  fingerprint: string;
  input: AseobiPaymentMetadataRequest;
  quote?: Quote;
};
const newKey = () =>
  `asoebi-${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random()
    .toString(36)
    .slice(2)}`;

/** A retry reuses its checkout identity. Closing a screen never cancels a hold. */
export function usePackageCheckout(
  eventId: string,
  groupId: string,
  userId: string,
  storage: CheckoutStorage
) {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [restored, setRestored] = useState(false);
  const [error, setError] = useState("");
  const [clock, setClock] = useState(Date.now());
  const attempt = useRef<Attempt | null>(null);
  const inFlight = useRef(false);
  const storageKey = `asoebi-package-checkout:${eventId}:${groupId}:${userId}`;
  const [create, { loading }] = useMutation(PackageCheckout);
  const [cancel, { loading: cancelling }] = useMutation(CancelPackageCheckout);
  const {
    data,
    error: statusError,
    refetch,
    stopPolling,
    startPolling,
  } = useQuery(GetPackageCheckoutStatus, {
    variables: { eventId, reference: quote?.payment_reference ?? "" },
    skip: !quote?.payment_reference,
    pollInterval: quote ? 10000 : 0,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: false,
  });
  const status =
    data?.getAsoebiCheckoutStatus?.payment_reference ===
    quote?.payment_reference
      ? data?.getAsoebiCheckoutStatus.status ?? "PENDING"
      : "PENDING";
  const terminal = status === "PAID";
  const expired = isExpired(quote?.expires_at, clock);
  useEffect(() => {
    let live = true;
    setRestored(false);
    setQuote(null);
    attempt.current = null;
    void storage
      .getItem(storageKey)
      .then((raw) => {
        if (!live || !raw) return;
        const value = JSON.parse(raw) as Attempt;
        if (value.key && value.fingerprint) {
          attempt.current = value;
          setQuote(value.quote ?? null);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (live) setRestored(true);
      });
    return () => {
      live = false;
    };
  }, [storageKey, storage]);
  useEffect(() => {
    if (!quote) return;
    setClock(Date.now());
    const timer = setInterval(() => setClock(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [quote]);
  useEffect(() => {
    if (terminal || !quote) stopPolling();
    else startPolling(10000);
  }, [terminal, quote, stopPolling, startPolling]);
  async function persist(value: Attempt) {
    attempt.current = value;
    await storage.setItem(storageKey, JSON.stringify(value));
  }
  async function prepare(input: AseobiPaymentMetadataRequest) {
    if (inFlight.current || !restored) return null;
    inFlight.current = true;
    setError("");
    try {
      if (!userId) throw new Error("Please sign in to purchase a package.");
      if (quote) return quote;
      const fingerprint = JSON.stringify(input);
      if (attempt.current && attempt.current.fingerprint !== fingerprint)
        throw new Error(
          "A checkout request is already in progress. Retry the same selection to recover it before changing your order."
        );
      const value = attempt.current ?? { key: newKey(), fingerprint, input };
      await persist(value);
      const result = await create({
        variables: { eventId, input: { ...input, idempotency_key: value.key } },
      });
      const next = result.data?.generateAsoebiPaymentMetadata;
      if (!next?.payment_reference || !next.expires_at)
        throw new Error(
          "Checkout could not be prepared. Retry to recover your reservation."
        );
      setQuote(next);
      await persist({ ...value, quote: next });
      return next;
    } catch (e) {
      const gqlError = e as {
        graphQLErrors?: { extensions?: { code?: string } }[];
        errors?: { extensions?: { code?: string } }[];
      };
      const code =
        gqlError.graphQLErrors?.[0]?.extensions?.code ??
        gqlError.errors?.[0]?.extensions?.code;
      if (
        code &&
        [
          "CHECKOUT_EXPIRED",
          "PACKAGE_VERSION_CONFLICT",
          "INSUFFICIENT_STOCK",
          "VALIDATION_ERROR",
          "FORBIDDEN",
          "CURRENCY_MISMATCH",
          "ASOEBI_DEADLINE_PASSED",
          "PACKAGE_NOT_AVAILABLE",
        ].includes(code)
      ) {
        attempt.current = null;
        await storage.removeItem(storageKey).catch(() => {});
      }
      setError(packageError(e));
      return null;
    } finally {
      inFlight.current = false;
    }
  }
  async function abandon() {
    setError("");
    try {
      if (quote) {
        const latest = await refetch();
        const outcome = latest.data?.getAsoebiCheckoutStatus.status;
        if (outcome === "PAID")
          throw new Error(
            "Payment has already been received. Check the order status."
          );
        const result = await cancel({
          variables: { eventId, reference: quote.payment_reference },
        });
        if (!result.data?.cancelAsoebiCheckout)
          throw new Error(
            "Unable to cancel this reservation. Check its payment status."
          );
      }
      if (!quote && attempt.current)
        throw new Error(
          "Recover your checkout first so its payment status can be checked."
        );
      await storage.removeItem(storageKey);
      attempt.current = null;
      setQuote(null);
    } catch (e) {
      setError(packageError(e));
    }
  }
  async function finish() {
    if (status !== "PAID") return;
    await storage.removeItem(storageKey);
    attempt.current = null;
    setQuote(null);
  }
  return {
    recover: () =>
      attempt.current?.input
        ? prepare(attempt.current.input)
        : Promise.resolve(null),
    finish,
    quote,
    status,
    terminal,
    expired,
    remainingSeconds: quote?.expires_at
      ? Math.max(0, Math.ceil((Date.parse(quote.expires_at) - clock) / 1000))
      : 0,
    loading,
    cancelling,
    restored,
    error:
      error ||
      (statusError
        ? "Unable to confirm your payment status. Please check again before paying."
        : ""),
    setError,
    prepare,
    abandon,
    check: () => refetch(),
    locked: !!quote || !!attempt.current,
  };
}
